import { observable } from 'mobx';
import Computation from './Computation';
import ValueType from '../../Value/Type/ValueType';
import Dependency from '../../Parameter/Dependency';
import AutomationDependencyContext from '../../AutomationDependencyContext';
import Validation from '../../Validation/Validation';
import PrimitiveValueType from '../../Value/Type/PrimitiveValueType';
import PrimitiveValue from '../../Value/PrimitiveValue';
import { loadModuleDirectly } from '../../../../@Util/DependencyInjection/Injection/DependencyInjection';
import { DataObjectStore } from '../../../../@Component/Domain/DataObject/DataObjectStore';
import getComputationFromDescriptor from '../../Api/getComputationFromDescriptor';
import Value from '../../Value/Value';
import FunctionContext from '../FunctionContext';
import safelyApplyFunction from '../../Api/safelyApplyFunction';
import safelySynchronousApplyFunction from '../../Api/safelySynchronousApplyFunction';
import { DataObject } from '../../../../@Component/Domain/DataObject/Model/DataObject';
import EmptyValue from '../../Value/EmptyValue';
import { NumberType } from '../../../../@Component/Domain/DataObject/Type/Number/NumberType';
import localizeText from '../../../Localization/localizeText';

export default class IsDutchBsnValidComputation extends Computation<ValueType<any>, Value<any, any>>
{
    // ------------------------- Properties -------------------------

    @observable.ref value: Computation<any, any>;

    // ------------------------ Constructor -------------------------

    constructor(value: Computation<any, any>)
    {
        super();

        this.value = value;
    }

    // ----------------------- Initialization -----------------------

    // -------------------------- Computed --------------------------

    // -------------------------- Actions ---------------------------

    // ------------------------ Public logic ------------------------

    getType(): ValueType<any>
    {
        return new PrimitiveValueType(
            loadModuleDirectly(DataObjectStore)
                .getTypeById('Boolean'));
    }

    getName(): string
    {
        return localizeText(
            'BSNNumberValidation',
            'BSN nummer validatie...'
        );
    }

    validate(): Validation[]
    {
        return this.value.validate();
    }

    isAsync(): boolean
    {
        return this.value.isAsync();
    }

    async apply(context: FunctionContext): Promise<Value<any, any>>
    {
        const value = await safelyApplyFunction(this.value, context);

        return this.getIsBsnValidValue(value);
    }

    synchronousApply(context: FunctionContext): PrimitiveValue
    {
        const value = safelySynchronousApplyFunction(this.value, context);

        return this.getIsBsnValidValue(value);
    }

    getIsBsnValidValue(value: Value<any, any>): Value<any, any>
    {
        if (value instanceof PrimitiveValue
            && value.value.type instanceof NumberType)
        {
            const number: number = value.value.value;
            const isValid: boolean = this.isBsnValid(number);

            return new PrimitiveValue(
                DataObject.constructFromTypeIdAndValue(
                    'Boolean',
                    isValid));
        }
        else
        {
            return EmptyValue.instance;
        }
    }

    // https://gist.github.com/barend/1018771
    isBsnValid(candidate: number)
    {
        if (candidate <= 9999999 || candidate > 999999999)
        {
            return false;
        }

        let sum = -1 * candidate % 10;

        for (let multiplier = 2; candidate > 0; multiplier++)
        {
            let val = (candidate /= 10) % 10;
            sum += multiplier * val;
        }

        return sum !== 0 && sum % 11 === 0;
    }

    augmentDescriptor(descriptor: any)
    {
        descriptor.type = 'IsDutchBsnValid';
        descriptor.value = this.value.toDescriptor();
    }

    getDependencies(): Dependency[]
    {
        return this.value.getDependencies();
    }

    static async fromDescriptor(descriptor: any,
                                dependencyContext: AutomationDependencyContext)
    {
        return new IsDutchBsnValidComputation(
            await getComputationFromDescriptor(
                descriptor.value,
                dependencyContext));
    }

    // ----------------------- Private logic ------------------------
}
